import { useCallback, useMemo, useRef, useState } from 'react';
import { portalService } from '../../services/portal';
import type {
  OrderDocumentCategory,
  OrderDocumentItem,
} from '../../types/portal';
import {
  DOC_TABS,
  classifyDoc,
  type DocTabId,
} from './DocumentsPanel.utils';
import InvoiceDetailModal from './InvoiceDetailModal';
import { apiPath, downloadAuthedFile, openAuthedFile } from '../../services/authedFile';

const UPLOAD_ACCEPT =
  'application/pdf,image/jpeg,image/png,image/heic,image/heif,image/webp';
const MAX_UPLOAD_MB = 25;

export type DocumentsPanelScope =
  | { kind: 'order'; orderId: string }
  | { kind: 'customer'; customerNo: string };

export interface DocumentsPanelProps {
  scope: DocumentsPanelScope;
  documents: OrderDocumentItem[];
  loading: boolean;
  onRefetch: () => void;
}

const formatSize = (bytes: number | null): string => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const formatCurrency = (val: number | null | undefined): string => {
  if (val == null) return '';
  return val.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const DocumentsPanel: React.FC<DocumentsPanelProps> = ({
  scope,
  documents,
  loading,
  onRefetch,
}) => {
  const [activeTab, setActiveTab] = useState<DocTabId>(() =>
    pickDefaultTab(documents),
  );
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [openInvoiceNo, setOpenInvoiceNo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Group documents by their (now type-aware) tab id once per render.
  const grouped = useMemo(() => {
    const g: Record<DocTabId, OrderDocumentItem[]> = {
      email: [],
      attachment: [],
      bol: [],
      sia: [],
      shipping: [],
      post_shipment: [],
      other: [],
      invoice: [],
    };
    for (const doc of documents) g[classifyDoc(doc)].push(doc);
    return g;
  }, [documents]);

  // Recompute the active tab when the data shape changes (e.g. first
  // fetch resolves). Falls back to the first non-empty tab so the user
  // doesn't land on an empty tab on initial render.
  const tabsWithData = useMemo(
    () => DOC_TABS.filter((t) => grouped[t.id].length > 0),
    [grouped],
  );
  const effectiveTab: DocTabId =
    grouped[activeTab].length > 0
      ? activeTab
      : (tabsWithData[0]?.id ?? activeTab);

  const activeTabMeta = DOC_TABS.find((t) => t.id === effectiveTab)!;
  const activeRows = grouped[effectiveTab];

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const category = activeTabMeta.uploadCategory;
      if (!category) return; // Upload-disabled tabs (email/attachment/invoice).
      const list = Array.from(files);
      if (list.length === 0) return;
      setUploadError(null);
      setUploading(true);
      try {
        for (const file of list) {
          if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
            throw new Error(
              `${file.name} exceeds the ${MAX_UPLOAD_MB} MiB limit.`,
            );
          }
          if (scope.kind === 'order') {
            await portalService.uploadOrderDocument(
              scope.orderId,
              file,
              category as OrderDocumentCategory,
            );
          } else {
            await portalService.uploadCustomerDocument(
              scope.customerNo,
              file,
              category as OrderDocumentCategory,
            );
          }
        }
        onRefetch();
      } catch (err) {
        const apiDetail = (err as { response?: { data?: { detail?: string } } })
          .response?.data?.detail;
        setUploadError(
          apiDetail || (err as Error).message || 'Upload failed.',
        );
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [activeTabMeta.uploadCategory, scope, onRefetch],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        void handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles],
  );

  const handleDelete = useCallback(
    async (doc: OrderDocumentItem) => {
      if (!window.confirm(`Remove "${doc.filename}" from this order?`))
        return;
      try {
        await portalService.deleteUploadedDocument(doc.id);
        onRefetch();
      } catch (err) {
        const apiDetail = (err as { response?: { data?: { detail?: string } } })
          .response?.data?.detail;
        setUploadError(
          apiDetail || (err as Error).message || 'Delete failed.',
        );
      }
    },
    [onRefetch],
  );

  const allowsUpload = activeTabMeta.uploadCategory != null;

  if (loading) {
    return (
      <div
        style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}
        data-testid="documents-panel-loading"
      >
        Loading documents…
      </div>
    );
  }

  return (
    <div data-testid="documents-panel">
      {/* Inner tab bar — one tab per doc type */}
      <div
        role="tablist"
        style={{
          display: 'flex',
          gap: 0,
          padding: '0 24px',
          borderBottom: '1px solid #e5e7eb',
          overflowX: 'auto',
          flexWrap: 'nowrap',
        }}
      >
        {DOC_TABS.map((tab) => {
          const count = grouped[tab.id].length;
          if (count === 0 && tab.uploadCategory == null) return null;
          const active = tab.id === effectiveTab;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`documents-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${active ? '#4338ca' : 'transparent'}`,
                color: active ? '#4338ca' : '#6b7280',
                fontSize: '13px',
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
              <span
                style={{
                  marginLeft: '6px',
                  padding: '0 6px',
                  borderRadius: '8px',
                  background: active ? '#c7d2fe' : '#e5e7eb',
                  color: active ? '#312e81' : '#374151',
                  fontSize: '11px',
                  fontWeight: 600,
                  display: 'inline-block',
                  minWidth: '18px',
                  textAlign: 'center',
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Per-tab drop-zone (only for tabs that accept uploads) */}
      {allowsUpload && (
        <div style={{ padding: '16px 24px 0' }}>
          <div
            data-testid="document-upload-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            style={{
              border: `2px dashed ${dragActive ? '#3b82f6' : '#d1d5db'}`,
              background: dragActive ? '#eff6ff' : '#fafafa',
              borderRadius: '8px',
              padding: '14px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '13px',
              marginBottom: '12px',
            }}
          >
            <div style={{ fontSize: '20px', marginBottom: '4px' }}>📤</div>
            <div style={{ fontWeight: 500, color: '#374151' }}>
              {uploading ? 'Uploading…' : activeTabMeta.dropzonePrompt}
            </div>
            <div
              style={{
                marginTop: '6px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  fontSize: '12px',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                }}
              >
                Choose file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={UPLOAD_ACCEPT}
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files) void handleFiles(e.target.files);
                }}
              />
            </div>
            <div style={{ marginTop: '6px', fontSize: '11px' }}>
              PDF, JPG, PNG · up to {MAX_UPLOAD_MB} MiB
            </div>
            {uploadError && (
              <div
                data-testid="document-upload-error"
                style={{
                  marginTop: '8px',
                  color: '#b91c1c',
                  fontSize: '12px',
                }}
              >
                {uploadError}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active tab body */}
      <div style={{ padding: allowsUpload ? '0 24px 16px' : '16px 24px' }}>
        {activeRows.length === 0 ? (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              color: '#6b7280',
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
            <div style={{ fontWeight: 500 }}>{activeTabMeta.emptyTitle}</div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              {activeTabMeta.emptyHint}
            </div>
          </div>
        ) : scope.kind === 'customer' ? (
          <GroupedByOrder
            rows={activeRows}
            onDelete={(doc) => void handleDelete(doc)}
            onOpenInvoice={setOpenInvoiceNo}
          />
        ) : (
          activeRows.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              onDelete={() => void handleDelete(doc)}
              onOpenInvoice={setOpenInvoiceNo}
            />
          ))
        )}
      </div>
      <InvoiceDetailModal
        invoiceNo={openInvoiceNo}
        onClose={() => setOpenInvoiceNo(null)}
      />
    </div>
  );
};

interface GroupedByOrderProps {
  rows: OrderDocumentItem[];
  onDelete: (doc: OrderDocumentItem) => void;
  onOpenInvoice: (invoiceNo: string) => void;
}

/**
 * Customer-side rendering: split docs into "Customer-direct" + one
 * collapsible section per source order. Order-scoped docs (uploads,
 * BOLs, invoices) carry `source_order_id` + `source_order_label` from
 * the backend rollup so we can group them without re-fetching.
 */
const GroupedByOrder: React.FC<GroupedByOrderProps> = ({
  rows,
  onDelete,
  onOpenInvoice,
}) => {
  const { direct, byOrder, orderOrder } = useMemo(() => {
    const direct: OrderDocumentItem[] = [];
    const byOrder = new Map<string, OrderDocumentItem[]>();
    const labels = new Map<string, string>();
    const dates = new Map<string, string>();
    const seen: string[] = [];
    for (const doc of rows) {
      if (!doc.source_order_id) {
        direct.push(doc);
        continue;
      }
      const oid = doc.source_order_id;
      if (!byOrder.has(oid)) {
        byOrder.set(oid, []);
        seen.push(oid);
      }
      byOrder.get(oid)!.push(doc);
      if (doc.source_order_label) labels.set(oid, doc.source_order_label);
      if (doc.source_order_date) dates.set(oid, doc.source_order_date);
    }
    // Sort order groups by date desc — newest first so the most-recent
    // order is at the top of the list. Orders without a date fall to
    // the bottom (preserving discovery order among themselves).
    seen.sort((a, b) => {
      const da = dates.get(a);
      const db_ = dates.get(b);
      if (da && db_) return db_.localeCompare(da);
      if (da) return -1;
      if (db_) return 1;
      return 0;
    });
    return {
      direct,
      byOrder,
      orderOrder: seen.map((oid) => ({
        id: oid,
        label: labels.get(oid) || `Order ${oid.slice(0, 8)}`,
        date: dates.get(oid) || null,
      })),
    };
  }, [rows]);

  return (
    <>
      {direct.length > 0 && (
        <section style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              color: '#6b7280',
              letterSpacing: '0.05em',
              margin: '4px 0 6px',
            }}
          >
            Customer-direct ({direct.length})
          </div>
          {direct.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              onDelete={() => onDelete(doc)}
              onOpenInvoice={onOpenInvoice}
            />
          ))}
        </section>
      )}
      {orderOrder.map(({ id, label, date }) => {
        const items = byOrder.get(id)!;
        // Order-associated groups are collapsed by default — the order
        // date stays visible in the summary so the user can scan a
        // customer's order history without opening every group.
        return (
          <details
            key={id}
            data-testid={`documents-order-group-${id}`}
            style={{ marginBottom: 12 }}
          >
            <summary
              style={{
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
                color: '#6b7280',
                letterSpacing: '0.05em',
                padding: '4px 0 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'baseline',
                gap: '10px',
              }}
            >
              <span>{label}</span>
              {date && (
                <span
                  style={{
                    fontWeight: 500,
                    color: '#9ca3af',
                    textTransform: 'none',
                    letterSpacing: 'normal',
                  }}
                >
                  {formatDate(date)}
                </span>
              )}
              <span style={{ marginLeft: 'auto', color: '#9ca3af' }}>
                ({items.length})
              </span>
            </summary>
            {items.map((doc) => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                onDelete={() => onDelete(doc)}
                onOpenInvoice={onOpenInvoice}
              />
            ))}
          </details>
        );
      })}
    </>
  );
};

interface DocumentRowProps {
  doc: OrderDocumentItem;
  onDelete: () => void;
  onOpenInvoice: (invoiceNo: string) => void;
}

/** The saved-as name for a Sage invoice PDF row: the attachment's own
 *  filename (last path segment of the download URL), not the row title. */
function sagePdfFilename(doc: OrderDocumentItem): string {
  const path = doc.download_url.split('?')[0];
  const last = path.substring(path.lastIndexOf('/') + 1);
  try {
    return last ? decodeURIComponent(last) : doc.filename;
  } catch {
    return doc.filename;
  }
}

const ICON_BY_TYPE: Record<string, string> = {
  source_email: '✉',
  email_attachment: '📎',
  bol: '📜',
  uploaded: '📄',
  invoice: '🧾',
};

const DocumentRow: React.FC<DocumentRowProps> = ({
  doc,
  onDelete,
  onOpenInvoice,
}) => {
  const icon = ICON_BY_TYPE[doc.doc_type] ?? '📄';
  const isInvoice = doc.doc_type === 'invoice';
  const isUploaded = doc.doc_type === 'uploaded';
  const isBol = doc.doc_type === 'bol';
  // An invoice row with a preview_url is the Sage-printed PDF (the sales
  // site's emailed copy); without one it is only openable as the portal's
  // own rendering via the Open button.
  const isSagePdfInvoice = isInvoice && !!doc.preview_url;
  const usesAuthedActions = isBol || isSagePdfInvoice;

  return (
    <div
      data-testid={`document-row-${doc.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 12px',
        background: '#f9fafb',
        borderRadius: '6px',
        marginBottom: '6px',
        border: '1px solid #e5e7eb',
      }}
    >
      <span style={{ fontSize: '18px' }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 500,
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {doc.filename}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: '#9ca3af',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {doc.mime_type && <span>{doc.mime_type.split('/').pop()}</span>}
          {doc.size_bytes != null && <span>{formatSize(doc.size_bytes)}</span>}
          {doc.status && (
            <span
              style={{
                textTransform: 'uppercase',
                fontWeight: 600,
                color: doc.status === 'final' ? '#059669' : '#d97706',
              }}
            >
              {doc.status}
            </span>
          )}
          {isInvoice && doc.invoice_total != null && (
            <span style={{ fontWeight: 600, color: '#065f46' }}>
              {formatCurrency(doc.invoice_total)}
            </span>
          )}
          {isUploaded && (
            <span
              style={{
                fontWeight: 600,
                color: '#92400e',
                background: '#fef3c7',
                padding: '1px 6px',
                borderRadius: '3px',
                fontSize: '10px',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Uploaded
            </span>
          )}
          {doc.created_at && <span>{formatDate(doc.created_at)}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        {usesAuthedActions ? (
          /* BOL bytes come from a bearer-protected endpoint (our GCS copy,
             Drive fallback), and the Sage invoice PDF from the email
             attachment proxy. A bare href sends no token → 401, and the
             Drive viewer needs Drive folder access, so both actions fetch
             the blob through the api client. */
          <>
            <button
              type="button"
              data-action={isInvoice ? 'invoice_sage_pdf_view' : 'bol_view'}
              data-target-invoice-no={isInvoice ? doc.invoice_no ?? undefined : undefined}
              data-outcome="open"
              onClick={() => {
                void openAuthedFile(apiPath(doc.preview_url ?? doc.download_url)).catch(() =>
                  window.alert('Could not open this document. Please try again.'),
                );
              }}
              style={{
                fontSize: '12px',
                color: '#3b82f6',
                padding: '4px 8px',
                borderRadius: '4px',
                background: '#eff6ff',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              View
            </button>
            <button
              type="button"
              data-action={isInvoice ? 'invoice_sage_pdf_download' : 'bol_download'}
              data-target-invoice-no={isInvoice ? doc.invoice_no ?? undefined : undefined}
              data-outcome="request"
              onClick={() => {
                void downloadAuthedFile(
                  apiPath(doc.download_url),
                  isSagePdfInvoice ? sagePdfFilename(doc) : doc.filename,
                ).catch(() =>
                  window.alert('Could not download this document. Please try again.'),
                );
              }}
              style={{
                fontSize: '12px',
                color: '#374151',
                padding: '4px 8px',
                borderRadius: '4px',
                background: '#f3f4f6',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Download
            </button>
          </>
        ) : (
          <>
            {doc.preview_url && doc.doc_type !== 'source_email' && (
              <a
                href={doc.preview_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '12px',
                  color: '#3b82f6',
                  textDecoration: 'none',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  background: '#eff6ff',
                }}
              >
                View
              </a>
            )}
            {doc.doc_type !== 'source_email' && doc.doc_type !== 'invoice' && (
              /* No target="_blank": the server responds with
                 Content-Disposition: attachment, so the browser saves the
                 file in place instead of opening a tab. The download
                 attribute is a same-origin fallback hint. */
              <a
                href={doc.download_url}
                download={doc.filename}
                style={{
                  fontSize: '12px',
                  color: '#374151',
                  textDecoration: 'none',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  background: '#f3f4f6',
                }}
              >
                Download
              </a>
            )}
          </>
        )}
        {isInvoice && doc.invoice_no && (
          <button
            type="button"
            data-testid={`invoice-open-${doc.invoice_no}`}
            onClick={() => onOpenInvoice(doc.invoice_no as string)}
            style={{
              fontSize: '12px',
              color: '#3b82f6',
              textDecoration: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              background: '#eff6ff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Open
          </button>
        )}
        {doc.deletable && (
          <button
            type="button"
            data-testid={`document-delete-${doc.id}`}
            onClick={onDelete}
            style={{
              fontSize: '12px',
              color: '#b91c1c',
              padding: '4px 8px',
              borderRadius: '4px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              cursor: 'pointer',
            }}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

function pickDefaultTab(docs: OrderDocumentItem[]): DocTabId {
  for (const tab of DOC_TABS) {
    if (docs.some((d) => classifyDoc(d) === tab.id)) return tab.id;
  }
  return 'email';
}

export default DocumentsPanel;
